import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    LinearProgress,
    Avatar,
    Grid,
    Pagination, FormControl, InputLabel, Select, Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import {
    TrendingUp as TrendingUpIcon,
    Assignment as AssignmentIcon,
    Timeline as TimelineIcon,
    AccessAlarm as AccessAlarmIcon,
    GroupAdd as GroupAddIcon,
    AssignmentTurnedIn as AssignmentTurnedInIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    MoreVert as MoreVertIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import ProjectDetails from '../../Employee/Projects/ProjectDetails';

const AllProjects = ({
    projectDetailsFromApi,
    noProjectsFoundError,
    onProjectClick,
    onViewTasks,
    paginationData,
    onPageChange,
    handleCardClick,
    selectedCategory,
    handleEdit,
    handleDelete,
    onRowsPerPageChange,
    rowsPerPage,
}) => {
    const [cardData, setCardData] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedProject, setSelectedProject] = useState(null);
    const [viewProjectDetails, setViewProjectDetails] = useState(false);
    const [selectedProjectDetailsIDForView, setSelectedProjectDetailsIDForView] = useState(null);
    const isLoading = !projectDetailsFromApi && !noProjectsFoundError;
    const userType = localStorage.getItem('userType')

    const getFirstLetter = (projectName) => {
        return projectName?.charAt(0)?.toUpperCase() || '';
    };

    useEffect(() => {
        const updateCardData = () => {
            setCardData([
                {
                    title: 'All Projects',
                    count: paginationData?.total_project_count || 0,
                    icon: <TrendingUpIcon sx={{ fontSize: 40, color: 'white' }} />,
                    gradient: 'linear-gradient(135deg, #6366F1 0%, #A855F7 100%)',
                    key: 'all'
                },
                {
                    title: 'Todo',
                    count: paginationData?.todo_project_count || 0,
                    icon: <AssignmentIcon sx={{ fontSize: 40, color: 'white' }} />,
                    gradient: 'linear-gradient(135deg, #FF6B6B 0%, #FFE66D 100%)',
                    key: 'todo'
                },
                {
                    title: 'Ongoing',
                    count: paginationData?.inprogress_count || 0,
                    icon: <TimelineIcon sx={{ fontSize: 40, color: 'white' }} />,
                    gradient: 'linear-gradient(135deg, #4ECDC4 0%, #556270 100%)',
                    key: 'ongoing'
                },
                {
                    title: 'Overdue',
                    count: paginationData?.overdue_count || 0,
                    icon: <AccessAlarmIcon sx={{ fontSize: 40, color: 'white' }} />,
                    gradient: 'linear-gradient(135deg, #FFA000 0%, #FFC107 100%)',
                    key: 'overdue'
                }
            ]);
        };

        updateCardData();
    }, [paginationData]);

    if (isLoading) {
        return <LinearProgress />;
    }

    const handleMenuOpen = (event, project) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedProject(project);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedProject(null);
    };

    const handleViewProjectDetails = (id) => {
        setViewProjectDetails(true);
        handleMenuClose();
        setSelectedProjectDetailsIDForView(id);
    }

    // Single Menu component with conditional rendering based on userType
    const renderMenuItems = () => {
        if (userType === 'Team Lead') {
            return (
                <>
                    <MenuItem
                        onClick={() => {
                            onViewTasks(selectedProject?.id);
                            handleMenuClose();
                        }}
                    >
                        <ListItemIcon>
                            <AssignmentTurnedInIcon sx={{ fontSize: 18, color: '#10b981' }} />
                        </ListItemIcon>
                        <ListItemText>View Tasks</ListItemText>
                    </MenuItem>
                    <MenuItem
                        onClick={() => handleViewProjectDetails(selectedProject?.id)}
                    >
                        <ListItemIcon>
                            <InfoIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
                        </ListItemIcon>
                        <ListItemText>View Project Details</ListItemText>
                    </MenuItem>
                </>
            );
        } else {
            // Manager/Admin menu
            return (
                <>
                    <MenuItem
                        onClick={(e) => {
                            e.stopPropagation();
                            onProjectClick(selectedProject);
                            handleMenuClose();
                        }}
                    >
                        <ListItemIcon>
                            <GroupAddIcon sx={{ fontSize: 18, color: '#6366f1' }} />
                        </ListItemIcon>
                        <ListItemText>Assign Team</ListItemText>
                    </MenuItem>

                    <MenuItem
                        onClick={() => {
                            onViewTasks(selectedProject?.id);
                            handleMenuClose();
                        }}
                    >
                        <ListItemIcon>
                            <AssignmentTurnedInIcon sx={{ fontSize: 18, color: '#10b981' }} />
                        </ListItemIcon>
                        <ListItemText>View Tasks</ListItemText>
                    </MenuItem>

                    <MenuItem
                        onClick={() => handleViewProjectDetails(selectedProject?.id)}
                    >
                        <ListItemIcon>
                            <InfoIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
                        </ListItemIcon>
                        <ListItemText>View Project Details</ListItemText>
                    </MenuItem>

                    <MenuItem
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(selectedProject?.id);
                            handleMenuClose();
                        }}
                    >
                        <ListItemIcon>
                            <EditIcon sx={{ fontSize: 18, color: '#64748b' }} />
                        </ListItemIcon>
                        <ListItemText>Edit</ListItemText>
                    </MenuItem>

                    <MenuItem
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(selectedProject?.id);
                            handleMenuClose();
                        }}
                        sx={{ color: '#ef4444' }}
                    >
                        <ListItemIcon>
                            <DeleteIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                        </ListItemIcon>
                        <ListItemText>Delete</ListItemText>
                    </MenuItem>
                </>
            );
        }
    };

    return (
        <div>
            <Grid container spacing={3} sx={{ mb: 4, px: { xs: 1, md: 0 } }}>
                {cardData && cardData.map((card) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={card.title}>
                        <Card
                            onClick={() => handleCardClick(card.key)}
                            sx={{
                                background: selectedCategory === card.key
                                    ? `linear-gradient(145deg, ${card.gradient}10, white)`
                                    : 'white',
                                borderRadius: '20px',
                                position: 'relative',
                                minHeight: '140px',
                                border: selectedCategory === card.key
                                    ? `2px solid ${card.gradient}`
                                    : '2px solid transparent',
                                transition: 'all 0.3s ease',
                                cursor: 'pointer',
                                overflow: 'hidden',
                                boxShadow: selectedCategory === card.key
                                    ? `0 10px 30px ${card.gradient}30`
                                    : '0 4px 6px rgba(0, 0, 0, 0.02)',
                                '&:hover': {
                                    transform: 'translateY(-5px)',
                                    boxShadow: `0 20px 40px ${card.gradient}20`,
                                    '& .card-count': {
                                        transform: 'scale(1.1)',
                                        color: card.gradient
                                    }
                                }
                            }}
                        >
                            <CardContent sx={{ p: 2.5 }}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    mb: 2
                                }}>
                                    <Box sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: '12px',
                                        background: card.gradient,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: `0 4px 8px ${card.gradient}40`
                                    }}>
                                        {React.cloneElement(card.icon, {
                                            sx: { fontSize: 22, color: 'white' }
                                        })}
                                    </Box>
                                    <Typography sx={{
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        color: '#1e293b'
                                    }}>
                                        {card.title}
                                    </Typography>
                                </Box>

                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    justifyContent: 'space-between'
                                }}>
                                    <Typography
                                        className="card-count"
                                        sx={{
                                            fontSize: '2.5rem',
                                            fontWeight: 700,
                                            color: '#0f172a',
                                            lineHeight: 1,
                                            transition: 'all 0.3s ease'
                                        }}
                                    >
                                        {card.count}
                                    </Typography>
                                    {selectedCategory === card.key && (
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            background: `${card.gradient}15`,
                                            py: 0.5,
                                            px: 1.5,
                                            borderRadius: '20px'
                                        }}>
                                            <Box sx={{
                                                width: 6,
                                                height: 6,
                                                borderRadius: '50%',
                                                background: card.gradient
                                            }} />
                                            <Typography sx={{
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                color: card.gradient
                                            }}>
                                                Active
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                <Box sx={{ mt: 2 }}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={selectedCategory === card.key ? 100 : 0}
                                        sx={{
                                            height: 4,
                                            borderRadius: 2,
                                            backgroundColor: '#f1f5f9',
                                            '& .MuiLinearProgress-bar': {
                                                background: card.gradient,
                                                transition: 'transform 0.4s ease'
                                            }
                                        }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <TableContainer
                component={Paper}
                sx={{
                    borderRadius: '12px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                    animation: 'fadeIn 0.3s ease-in-out',
                    overflowX: 'auto',
                }}
            >
                <Box sx={{
                    borderRadius: '24px',
                    overflow: 'hidden',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 0 20px rgba(0,0,0,0.05)',
                    display: { xs: 'none', md: 'block' }
                }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {[
                                    'Project', 'Manager', 'Team Lead',
                                    'Client', 'Priority', 'Progress',
                                    'Actions'
                                ].map((header) => (
                                    <TableCell
                                        key={header}
                                        sx={{
                                            backgroundColor: '#f8fafc',
                                            color: '#64748b',
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            padding: '16px 20px',
                                            borderBottom: '2px solid #e2e8f0',
                                            whiteSpace: 'nowrap',
                                            display: {
                                                xs: ['Progress', 'Client'].includes(header) ? 'none' : 'table-cell',
                                                lg: 'table-cell'
                                            },
                                            textAlign: {
                                                xs: ['Actions', 'Progress'].includes(header) ? 'center' : 'left',
                                                xl: 'left'
                                            }
                                        }}
                                    >
                                        {header}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography variant="h6" color="textSecondary">
                                            Loading projects...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : noProjectsFoundError ? (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography variant="h6" color="textSecondary">
                                            No projects found for this Status.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : projectDetailsFromApi?.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography variant="h6" color="textSecondary">
                                            No projects available
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                projectDetailsFromApi?.map((project) => (
                                    <TableRow
                                        key={project.id}
                                        sx={{
                                            '&:hover': {
                                                backgroundColor: '#fafafa',
                                                '& .action-cell': { opacity: 1 },
                                            },
                                            transition: 'all 0.2s ease',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <TableCell sx={{ padding: '10px' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, lg: 2 } }}>
                                                <Box
                                                    sx={{
                                                        width: '40px',
                                                        height: '40px',
                                                        borderRadius: '10px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        backgroundColor: '#f1f5f9',
                                                        color: '#3b82f6',
                                                    }}
                                                >
                                                    {getFirstLetter(project.project_title)}
                                                </Box>
                                                <Box>
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: '#1e293b',
                                                            fontSize: '14px',
                                                        }}
                                                    >
                                                        {project.project_title}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>

                                        <TableCell sx={{ padding: '10px' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Avatar
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        backgroundColor: '#e0f2fe',
                                                        color: '#0ea5e9',
                                                        fontSize: '14px',
                                                    }}
                                                >
                                                    {getFirstLetter(project.manager?.first_name)}
                                                </Avatar>
                                                <Typography sx={{ color: '#334155', fontSize: '14px' }}>
                                                    {project.manager
                                                        ? `${project.manager.first_name} ${project.manager.last_name}`
                                                        : 'not assigned'}
                                                </Typography>
                                            </Box>
                                        </TableCell>

                                        <TableCell sx={{ padding: '10px' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Avatar
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        backgroundColor: '#fae8ff',
                                                        color: '#d946ef',
                                                        fontSize: '14px',
                                                    }}
                                                >
                                                    {getFirstLetter(project.team_leader?.first_name)}
                                                </Avatar>
                                                <Typography sx={{ color: '#334155', fontSize: '14px' }}>
                                                    {project.team_leader
                                                        ? `${project.team_leader.first_name} ${project.team_leader.last_name}`
                                                        : 'not assigned'}
                                                </Typography>
                                            </Box>
                                        </TableCell>

                                        <TableCell sx={{ padding: '10px', display: { xs: 'none', lg: 'table-cell' } }}>
                                            {project.clients && project.clients.length > 0 ? (
                                                project.clients.map((client) => (
                                                    <Box
                                                        key={client.id}
                                                        sx={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            backgroundColor: '#f1f5f9',
                                                            borderRadius: '20px',
                                                            padding: '6px 12px',
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                color: '#334155',
                                                                fontSize: '13px',
                                                                fontWeight: 500,
                                                            }}
                                                        >
                                                            {`${client.first_name} ${client.last_name}`}
                                                        </Typography>
                                                    </Box>
                                                ))
                                            ) : (
                                                'No clients'
                                            )}
                                        </TableCell>

                                        <TableCell sx={{ padding: '10px' }}>
                                            <Box
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    borderRadius: '20px',
                                                    padding: '6px 12px',
                                                    ...{
                                                        High: { backgroundColor: '#fef2f2', color: '#ef4444' },
                                                        Medium: { backgroundColor: '#fefce8', color: '#eab308' },
                                                        Low: { backgroundColor: '#f0fdf4', color: '#22c55e' },
                                                    }[project.priority],

                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 6,
                                                        height: 6,
                                                        borderRadius: '50%',
                                                        backgroundColor: 'currentColor',

                                                    }}
                                                />
                                                <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                                                    {project.priority}
                                                </Typography>
                                            </Box>
                                        </TableCell>

                                        <TableCell sx={{ padding: '10px', display: { xs: 'none', lg: 'table-cell' } }}>
                                            <Box sx={{ position: 'relative', width: { lg: '60px', xl: '90px' } }}>
                                                <Box
                                                    sx={{
                                                        width: '100%',
                                                        height: '6px',
                                                        backgroundColor: '#f1f5f9',
                                                        borderRadius: '10px',
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            height: '100%',
                                                            width: `${project.progress}%`,
                                                            backgroundColor:
                                                                project.progress >= 80
                                                                    ? '#22c55e'
                                                                    : project.progress >= 40
                                                                        ? '#3b82f6'
                                                                        : '#ef4444',
                                                            transition: 'width 0.3s ease',
                                                        }}
                                                    />
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        position: 'absolute',
                                                        right: -25,
                                                        top: -5,
                                                        fontSize: '12px',
                                                        fontWeight: 600,
                                                        color: '#64748b',
                                                    }}
                                                >
                                                    {project.progress}%
                                                </Typography>
                                            </Box>
                                        </TableCell>


                                        <TableCell
                                            className="action-cell"
                                            sx={{
                                                padding: '10px',
                                                opacity: 1,
                                                transition: 'opacity 0.2s ease',
                                                textAlign: 'center'
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <IconButton
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleMenuOpen(e, project);
                                                }}
                                                sx={{
                                                    width: { xs: 34, md: 28, lg: 34 },
                                                    height: { xs: 34, md: 28, lg: 34 },
                                                    '&:hover': { backgroundColor: '#f1f5f9' },
                                                }}
                                            >
                                                <MoreVertIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                            </IconButton>
                                        </TableCell>

                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>

                </Box>

                {/* Single Menu component */}
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    PaperProps={{
                        elevation: 3,
                        sx: {
                            borderRadius: '8px',
                            minWidth: '160px',
                        }
                    }}
                >
                    {renderMenuItems()}
                </Menu>

            </TableContainer>

            {/* Mobile Card View */}
            <Box sx={{
                display: { xs: 'block', sm: 'none' },
                p: 1,
                // For sm screens - two cards per row
                '@media (min-width: 600px) and (max-width: 899px)': {
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '16px'
                }
            }}>
                {isLoading ? (
                    <Box sx={{
                        textAlign: 'center',
                        py: 4,
                        gridColumn: '1 / -1' // Span all columns on sm
                    }}>
                        <Typography variant="h6" color="textSecondary">
                            Loading projects...
                        </Typography>
                    </Box>
                ) : noProjectsFoundError ? (
                    <Box sx={{
                        textAlign: 'center',
                        py: 4,
                        gridColumn: '1 / -1' // Span all columns on sm
                    }}>
                        <Typography variant="h6" color="textSecondary">
                            No projects found for this Status.
                        </Typography>
                    </Box>
                ) : projectDetailsFromApi?.length === 0 ? (
                    <Box sx={{
                        textAlign: 'center',
                        py: 4,
                        gridColumn: '1 / -1' // Span all columns on sm
                    }}>
                        <Typography variant="h6" color="textSecondary">
                            No projects available
                        </Typography>
                    </Box>
                ) : (
                    projectDetailsFromApi?.map((project) => (
                        <Card key={project.id} sx={{
                            mb: 2,
                            p: 2,
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            // On sm screens, remove margin bottom since gap handles spacing
                            '@media (min-width: 600px)': {
                                mb: 0
                            }
                        }}>
                            {/* Project Title */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <Box
                                    sx={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#f1f5f9',
                                        color: '#3b82f6',
                                    }}
                                >
                                    {getFirstLetter(project.project_title)}
                                </Box>
                                <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: '16px' }}>
                                    {project.project_title}
                                </Typography>
                            </Box>

                            {/* Project Details - Single Column Layout */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {/* Manager */}
                                <Box>
                                    <Typography variant="body2" color="textSecondary">Manager</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                        <Avatar sx={{ width: 24, height: 24, fontSize: '12px', backgroundColor: '#e0f2fe', color: '#0ea5e9' }}>
                                            {getFirstLetter(project.manager?.first_name)}
                                        </Avatar>
                                        <Typography sx={{ fontSize: '14px' }}>
                                            {project.manager ? `${project.manager.first_name} ${project.manager.last_name}` : 'Not assigned'}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Team Lead */}
                                <Box>
                                    <Typography variant="body2" color="textSecondary">Team Lead</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                        <Avatar sx={{ width: 24, height: 24, fontSize: '12px', backgroundColor: '#fae8ff', color: '#d946ef' }}>
                                            {getFirstLetter(project.team_leader?.first_name)}
                                        </Avatar>
                                        <Typography sx={{ fontSize: '14px' }}>
                                            {project.team_leader ? `${project.team_leader.first_name} ${project.team_leader.last_name}` : 'Not assigned'}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Clients */}
                                <Box>
                                    <Typography variant="body2" color="textSecondary">Clients</Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                                        {project.clients && project.clients.length > 0 ? (
                                            project.clients.map((client) => (
                                                <Chip
                                                    key={client.id}
                                                    label={`${client.first_name} ${client.last_name}`}
                                                    size="small"
                                                    sx={{ backgroundColor: '#f1f5f9', borderRadius: '20px' }}
                                                />
                                            ))
                                        ) : (
                                            <Typography sx={{ fontSize: '14px' }}>No clients</Typography>
                                        )}
                                    </Box>
                                </Box>

                                {/* Priority and Progress in one row */}
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    {/* Priority */}
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" color="textSecondary">Priority</Typography>
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                mt: 0.5,
                                                borderRadius: '20px',
                                                padding: '4px 8px',
                                                ...{
                                                    High: { backgroundColor: '#fef2f2', color: '#ef4444' },
                                                    Medium: { backgroundColor: '#fefce8', color: '#eab308' },
                                                    Low: { backgroundColor: '#f0fdf4', color: '#22c55e' },
                                                }[project.priority],
                                            }}
                                        >
                                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'currentColor' }} />
                                            <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                                                {project.priority}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Progress */}
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" color="textSecondary">Progress</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                            <Box sx={{ width: '60px', height: '6px', backgroundColor: '#f1f5f9', borderRadius: '10px', overflow: 'hidden' }}>
                                                <Box
                                                    sx={{
                                                        height: '100%',
                                                        width: `${project.progress}%`,
                                                        backgroundColor:
                                                            project.progress >= 80 ? '#22c55e' :
                                                                project.progress >= 40 ? '#3b82f6' : '#ef4444',
                                                    }}
                                                />
                                            </Box>
                                            <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                                                {project.progress}%
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Actions */}
                                <Box sx={{ mt: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                        {userType !== 'Team Lead' && (
                                            <Tooltip title="Assign Team">
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onProjectClick(project); }}>
                                                    <GroupAddIcon sx={{ fontSize: 18, color: '#6366f1' }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="View All Tasks">
                                            <IconButton size="small" onClick={() => onViewTasks(project.id)}>
                                                <AssignmentTurnedInIcon sx={{ fontSize: 18, color: '#10b981' }} />
                                            </IconButton>
                                        </Tooltip>
                                        {userType !== 'Team Lead' && (
                                            <>
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(project.id); }}>
                                                    <EditIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                                </IconButton>
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(project.id); }}>
                                                    <DeleteIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                                                </IconButton>
                                            </>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </Card>
                    ))
                )}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 3, mb: 3 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Rows per page</InputLabel>
                    <Select
                        value={rowsPerPage}
                        onChange={(e) => onRowsPerPageChange(e.target.value)}
                        label="Rows per page"
                    >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                    </Select>
                </FormControl>

                {!noProjectsFoundError && projectDetailsFromApi?.length > 0 && (
                    <Pagination
                        count={paginationData?.totalPages || 1}
                        page={paginationData?.currentPage || 1}
                        onChange={onPageChange}
                        color="primary"
                        sx={{
                            '& .MuiPaginationItem-root': {
                                borderRadius: '8px',
                                '&.Mui-selected': {
                                    background: 'linear-gradient(135deg, #6366F1 0%, #A855F7 100%)',
                                }
                            }
                        }}
                    />
                )}
            </Box>

            <ProjectDetails
                open={viewProjectDetails}
                onClose={() => setViewProjectDetails(false)}
                selectedProjectId={selectedProjectDetailsIDForView}
            />
        </div>
    );
};

export default AllProjects;